import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE } from '../api/config';

interface AuthState {
  isAuthenticated: boolean;
  user: any | null;
  isLoading: boolean;
  error: string | null;
}

export const useAuth = () => {
  const [authState, setAuthState] = useState<AuthState>({
    isAuthenticated: false,
    user: null,
    isLoading: true,
    error: null
  });

  const navigate = useNavigate();

  // Check authentication status on mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        setAuthState(prev => ({ ...prev, isLoading: true, error: null }));

        // Use the configured API_BASE instead of hardcoded localhost
        const cacheBuster = new Date().getTime();
        const token = localStorage.getItem('dashboard_token');
        const response = await fetch(`${API_BASE}/dashboard/check-auth?_=${cacheBuster}`, {
          credentials: 'include',
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0',
            'X-Requested-With': 'XMLHttpRequest',
            'Authorization': token ? `Bearer ${token}` : '',
            'Dashboard-Authorization': token ? `Bearer ${token}` : ''
          },
          mode: 'cors'
        });
        const data = await response.json();

        if (data && data.authenticated) {
          setAuthState({
            isAuthenticated: true,
            user: data.user,
            isLoading: false,
            error: null
          });
        } else {
          setAuthState({
            isAuthenticated: false,
            user: null,
            isLoading: false,
            error: null
          });
          navigate('/login');
        }
      } catch (error) {
        setAuthState({
          isAuthenticated: false,
          user: null,
          isLoading: false,
          error: 'Failed to check authentication status'
        });
        navigate('/login');
      }
    };

    checkAuth();
  }, [navigate]);

  // Login function
  const handleLogin = useCallback(async (username: string, password: string) => {
    try {
      setAuthState(prev => ({ ...prev, isLoading: true, error: null }));

      // Use the configured API_BASE instead of hardcoded localhost
      const response = await fetch(`${API_BASE}/dashboard/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          'Origin': window.location.origin
        },
        credentials: 'include',
        body: JSON.stringify({ username, password }),
        mode: 'cors'
      });

      const data = await response.json();

      if (data && data.success) {
        // Store token in localStorage as a fallback
        if (data.token) {
          localStorage.setItem('dashboard_token', data.token);
          console.log('Token stored in localStorage');

          // Also set as cookie manually as a fallback
          document.cookie = `dashboard_token=${data.token}; path=/; max-age=${8 * 60 * 60}`;
          console.log('Token also stored as cookie manually');
        }

        setAuthState({
          isAuthenticated: true,
          user: data.user,
          isLoading: false,
          error: null
        });
        navigate('/');
        return true;
      } else {
        setAuthState(prev => ({
          ...prev,
          isLoading: false,
          error: data.message || data.error || 'Login failed'
        }));
        return false;
      }
    } catch (error: any) {
      console.error('Login error:', error);
      setAuthState(prev => ({
        ...prev,
        isLoading: false,
        error: error.message || 'Login failed'
      }));
      return false;
    }
  }, [navigate]);

  // Logout function
  const handleLogout = useCallback(async () => {
    try {
      setAuthState(prev => ({ ...prev, isLoading: true }));

      // Use the configured API_BASE instead of hardcoded localhost
      const token = localStorage.getItem('dashboard_token');
      await fetch(`${API_BASE}/dashboard/logout`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          'Authorization': token ? `Bearer ${token}` : '',
          'Dashboard-Authorization': token ? `Bearer ${token}` : '',
          'Origin': window.location.origin
        },
        mode: 'cors'
      });

      // Clear token from localStorage
      localStorage.removeItem('dashboard_token');

      // Clear token from cookie
      document.cookie = 'dashboard_token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';

      setAuthState({
        isAuthenticated: false,
        user: null,
        isLoading: false,
        error: null
      });
      navigate('/login');
    } catch (error) {
      setAuthState(prev => ({
        ...prev,
        isLoading: false,
        error: 'Logout failed'
      }));
    }
  }, [navigate]);

  return {
    ...authState,
    login: handleLogin,
    logout: handleLogout
  };
};
